/**************************************************************************************************************************************
 * 
 * MSAL implementation is inspred from msal-angular-v2-samples :
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-angular-v2-samples/angular12-sample-app
 * 
**************************************************************************************************************************************/


import { NgModule } from '@angular/core';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS, } from '@angular/common/http';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import {
  MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService
  , MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent
} from '@azure/msal-angular';
import { APP_BASE_HREF, PlatformLocation, CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { ErrorInterceptor } from 'src/app/core/interceptors/error.interceptor';
import { HttpLoaderFactory } from 'src/app/shared/constants/language';
import { LayoutModule } from 'src/app/modules/layout/layout.module';
import { APP_CONFIG } from 'src/app/core/services/app-config.service';
import { AppConfig } from 'src/app/shared/models/config-variable.model';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BlankComponent } from './modules/blank/blank.component';


export function loggerCallback(logLevel: LogLevel, message: string) {
  console.debugEx(message);
}

/**
 * This function is used internal to get a string instance of the `<base href="" />` value from `index.html`.
 * This is an exported function, instead of a private function or inline lambda, to prevent this error:
 *
 * `Error encountered resolving symbol values statically.`
 * `Function calls are not supported.`
 * `Consider replacing the function or lambda with a reference to an exported function.`
 *
 * @param platformLocation an Angular service used to interact with a browser's URL
 * @return a string instance of the `<base href="" />` value from `index.html`
 */
export function getBaseHref(platformLocation: PlatformLocation): string {
  return platformLocation.getBaseHrefFromDOM();
}

export function MSALInstanceFactory(config: AppConfig, platformLocation: PlatformLocation): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: config.authConfig.clientId,
      authority: config.authConfig.authority,
      //redirectUri: config.authConfig.redirectUri,
      //redirectUri: `${window.location.protocol}//${window.location.host}`,
      // if redirect URI starts with http let's take it  verbatim. If not then the redirecturi in the 
      // config file is a path and will be appended to tne running hostname
      redirectUri: `${config.authConfig.redirectUri.startsWith('http') ?
          config.authConfig.redirectUri.toLowerCase()
          : ((platformLocation as any)._location.origin
            + (config.authConfig.redirectUri.startsWith('/') ? '' : '/')
            + config.authConfig.redirectUri).toLowerCase()
        }`,
      //navigateToLoginRequestUrl: true, 
      postLogoutRedirectUri: 'ignored in this version'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(config: AppConfig): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(`${config.ApiUrl}*`, [config.ApiScope]);

  //protectedResourceMap.set(`${config.ApiUrl}*`, ['api://0c9fc662-af2a-45e9-9813-dd04abab691c/.default']); //This is for DEV
  //protectedResourceMap.set(`${config.ApiUrl}*`, ['api://747cdf48-246c-4bbb-9df7-92b4df72c381/.default']);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read', 'openid', 'profile', 'offline_access']
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [AppComponent, BlankComponent],
  imports: [
    LayoutModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    AppRoutingModule,
    HttpClientModule,
    ToastModule,
    FormsModule,
    DropdownModule,
    TranslateModule.forRoot({
      defaultLanguage: 'fr',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MsalModule,
  ],
  providers: [
    MessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [PlatformLocation]
    },
    MsalService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: (config: AppConfig, platformLocation: PlatformLocation) => MSALInstanceFactory(config, platformLocation),
      deps: [APP_CONFIG, PlatformLocation]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [APP_CONFIG]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: (config: AppConfig) => MSALInterceptorConfigFactory(config),
      deps: [APP_CONFIG]
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent] // MsalRedirectComponent bootstrapped here
})
export class AppModule {
  constructor(private router: Router,) {
    router.events.subscribe(
      console.logEx
    );
  }
}

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
var appConfig: AppConfig = new AppConfig();
